import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';

export interface FieldTask {
  id: string;
  type: 'bol' | 'rental_delivery' | 'rental_pickup' | 'rental_return' | 'inspection';
  title: string;
  subtitle?: string;
  meta?: string;
  status: 'scheduled' | 'in_progress' | 'completed';
  scheduledAt?: string;
  completedAt?: string;
  sourceRoute: string;
  vertical: 'trucking' | 'surface_rentals';
  customerName?: string;
  equipmentName?: string;
}

/**
 * The ACTUAL shape of `GET /field/today` — `{ driver, tasks }`.
 *
 * This interface previously declared `greeting`, `date`, `clockedIn` and
 * `clockedInAt` at the top level. None of them are there. `clockedIn` really
 * lives at `data.driver.clockedIn`, so MyWorkLayout's `data?.clockedIn ?? false`
 * read `undefined` and fell to `false` on every render — its Clock in/out toggle
 * was permanently labelled "Clock in" and could never call clock-out, even for a
 * worker who was on the clock. TypeScript could not catch it because the
 * interface asserted the same wrong field.
 *
 * `greeting` and `date` were never used — MyWorkLayout computes both locally.
 */
export interface FieldTodayResponse {
  driver: {
    id: string;
    name: string;
    positions?: unknown[];
    clockedIn: boolean;
    clockInAt: string | null;
  } | null;
  tasks: FieldTask[];
}

export function useFieldToday() {
  return useQuery<FieldTodayResponse>({
    queryKey: ['field-today'],
    queryFn: () => api.field.today().then((r) => r.data?.data ?? r.data),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useFieldUpcoming(days = 7) {
  return useQuery<FieldTask[]>({
    queryKey: ['field-upcoming', days],
    queryFn: () => api.field.upcoming(days).then((r) => r.data?.data?.tasks ?? r.data?.tasks ?? []),
    staleTime: 60_000,
  });
}
